import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useInstallPrompt, InstallInstructionsModal, detectPlatform, isStandalone } from "@/components/SavePageHero";

const DISMISS_KEY = "bs_pwa_popup_dismissed_at";
const SHOWN_THIS_SESSION_KEY = "bs_pwa_popup_shown_session";
const COOLDOWN_DAYS = 7;
const DELAY_MS = 8000;

function shouldShowPopup() {
  try {
    if (sessionStorage.getItem(SHOWN_THIS_SESSION_KEY)) return false;
    const last = localStorage.getItem(DISMISS_KEY);
    if (!last) return true;
    const lastTs = parseInt(last, 10) || 0;
    const sinceDays = (Date.now() - lastTs) / (1000 * 60 * 60 * 24);
    return sinceDays >= COOLDOWN_DAYS;
  } catch {
    return true;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, "1");
  } catch { /* ignore */ }
}

export default function SavePagePopup() {
  const [open, setOpen] = useState(false);
  const [showInstr, setShowInstr] = useState(false);
  const [platform] = useState(() => detectPlatform());
  const { available, triggerInstall } = useInstallPrompt();

  useEffect(() => {
    if (isStandalone()) return;
    if (!shouldShowPopup()) return;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, "1"); } catch { /* ignore */ }
      setOpen(true);
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    markDismissed();
    setOpen(false);
  };

  const handleSave = async () => {
    if (available) {
      const r = await triggerInstall();
      if (r.ok) {
        markDismissed();
        setOpen(false);
        return;
      }
    }
    setOpen(false);
    setShowInstr(true);
    markDismissed();
  };

  return (
    <>
      {open && (
        <div
          data-testid="save-page-popup"
          role="dialog"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-6 pointer-events-none"
        >
          <div className="mx-auto max-w-md pointer-events-auto bg-white border border-emerald-300 shadow-2xl rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="bg-emerald-900 px-5 py-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                <img src="/bs-logo-square.png" alt="Business Showers" className="w-full h-full object-contain p-0.5" />
              </div>
              <div className="text-white font-[Outfit] font-semibold text-sm leading-tight">
                Business Showers
                <div className="text-amber-200 text-[10px] uppercase tracking-[0.2em] font-bold mt-0.5">
                  Official Event Page
                </div>
              </div>
              <button
                type="button"
                data-testid="save-page-popup-close"
                onClick={close}
                aria-label="Close"
                className="ml-auto text-white/80 hover:text-white text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <p className="font-[Outfit] text-sm text-slate-800 leading-relaxed">
                Stay connected to the entire Business Showers experience! Save the official event page for
                live pitches, community voting, finalist and honoree announcements, tickets, event details,
                sponsorship and volunteer opportunities, the $10,000 award reveal, and more.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Button
                  data-testid="save-page-popup-save"
                  onClick={handleSave}
                  className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold h-10"
                >
                  Save This Event Page
                </Button>
                <Button
                  variant="outline"
                  data-testid="save-page-popup-later"
                  onClick={close}
                  className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 font-[Outfit] font-semibold h-10"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <InstallInstructionsModal open={showInstr} onOpenChange={setShowInstr} platform={platform} />
    </>
  );
}
